/**
 * Resolves which CRM types a credential may be granted, and the creator's read
 * scopings for each, partitioned public vs private.
 *
 * Owned by the create slideout rather than the picker because BOTH need it: the
 * picker drives its per-type Public/Private control from it, and the submit path
 * builds the grants from it. Computing it once in the parent and passing it down
 * keeps a single source of truth — an earlier version had the picker compute it
 * and push it up through an effect, which re-entered the parent's setState on
 * every render and ran the suite out of memory.
 */

import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';

import type {
  GrantableType,
  ReadScopings,
  WriteScopings,
} from './api-access-service';
import {
  CREDENTIAL_WRITE_EVENT_TYPES,
  deriveImpliedTypes,
  getGrantableTypes,
  getReadScopings,
  getWriteScopings,
  hasReadPermissionForType,
} from './api-access-service';

export interface GrantScopings {
  /** Types this user may grant, already filtered by allowed kind and read access. */
  grantable: GrantableType[];
  /** Read scopings per grantable type, partitioned by association target. */
  scopingsByType: ReadonlyMap<string, ReadScopings>;
  /** Association type name -> target entity type name (public/private classification). */
  associationTargetTypes: ReadonlyMap<string, string>;
  /**
   * Write scopings per grantable type, grouped by event type — already
   * narrowed to `allowedWriteEventsByType`, so the grant builders can never
   * emit a write event the type does not accept.
   */
  writeScopingsByType: ReadonlyMap<string, WriteScopings[]>;
  /**
   * The write events a grant on each type may carry: the schema's
   * `writable_event_types` for the type (synced types accept only association
   * events, or nothing), minus every type the credential property's
   * `write_excluded_entity_type_names` names (web visits and visitors are
   * ingestion facts, never editable here). Empty means the type is read-only
   * on this surface; the picker says so instead of offering a toggle that
   * would mint an inert grant.
   */
  allowedWriteEventsByType: ReadonlyMap<string, readonly string[]>;
  /**
   * Derive the types a selection needs but the picker cannot offer — schema
   * meta types, `ahoy_user`, `ahoy_org`, and the pipeline/stage types of any
   * selected case-kind type. Returned as labels for display and names for the
   * grant build, so the closure is shown rather than applied silently.
   */
  resolveImplied: (selectedTypeNames: readonly string[]) => {
    names: string[];
    labels: string[];
  };
  isLoading: boolean;
  error: unknown;
}

export function useGrantScopings(orgId: number): GrantScopings {
  const {
    schema,
    loading: schemaLoading,
    error: schemaError,
  } = usePropertySchema(orgId, 'ahoy_service_account');
  const {
    data,
    isLoading: objectTypesLoading,
    error: objectTypesError,
  } = useObjectTypes(orgId);
  const {
    associationTypeMap,
    isLoading: associationTypesLoading,
    error: associationTypesError,
  } = useAssociationTypes(orgId);
  const { currentOrg } = useOrg();

  const allowedKinds = useMemo(
    () => schema['ahoy_permissions']?.field_type_config?.entity_kinds ?? [],
    [schema]
  );
  const writeExcludedTypes = useMemo(
    () =>
      schema['ahoy_permissions']?.field_type_config
        ?.write_excluded_entity_type_names ?? [],
    [schema]
  );

  // `*_timeline` names alias the underlying entity type and are not valid
  // entity-type identifiers for grants. `allObjectTypes` is the raw, unfiltered
  // list, so exclude timeline aliases before building grantable types.
  const objectTypes = useMemo(
    () =>
      (data?.allObjectTypes ?? []).filter(
        t => !t.entity_type_name.endsWith('_timeline')
      ),
    [data]
  );

  const canRead = useCallback(
    (t: string) => hasReadPermissionForType(currentOrg?.permissions ?? [], t),
    [currentOrg?.permissions]
  );

  const grantable = useMemo(
    () => getGrantableTypes(objectTypes, allowedKinds, canRead),
    [objectTypes, allowedKinds, canRead]
  );

  const scopingsByType = useMemo(() => {
    const permissions = currentOrg?.permissions ?? [];
    return new Map<string, ReadScopings>(
      grantable.map(({ entityTypeName }) => [
        entityTypeName,
        getReadScopings(permissions, entityTypeName, associationTypeMap),
      ])
    );
  }, [grantable, currentOrg?.permissions, associationTypeMap]);

  // Resolved from the raw object-type list rather than `grantable` so the
  // schema's answer is read exactly once per type; `grantable` is the subset
  // that then indexes into it.
  const allowedWriteEventsByType = useMemo(() => {
    const excluded = new Set<string>(writeExcludedTypes);
    return new Map<string, readonly string[]>(
      objectTypes.map(type => [
        type.entity_type_name,
        excluded.has(type.entity_type_name)
          ? []
          : CREDENTIAL_WRITE_EVENT_TYPES.filter(
              eventType =>
                type.writable_event_types === undefined ||
                type.writable_event_types.includes(eventType)
            ),
      ])
    );
  }, [objectTypes, writeExcludedTypes]);

  const writeScopingsByType = useMemo(() => {
    const permissions = currentOrg?.permissions ?? [];
    return new Map<string, WriteScopings[]>(
      grantable.map(({ entityTypeName }) => {
        const allowed = new Set(
          allowedWriteEventsByType.get(entityTypeName) ?? []
        );
        return [
          entityTypeName,
          getWriteScopings(
            permissions,
            entityTypeName,
            associationTypeMap
          ).filter(scoping => allowed.has(scoping.eventTypeName)),
        ];
      })
    );
  }, [
    grantable,
    currentOrg?.permissions,
    associationTypeMap,
    allowedWriteEventsByType,
  ]);

  // Labels come from the FULL object-type list, not `grantable` — every implied
  // type is by definition one the picker filtered out, so it is absent there.
  const resolveImplied = useCallback(
    (selectedTypeNames: readonly string[]) => {
      const names = deriveImpliedTypes(selectedTypeNames, objectTypes, canRead);
      const labelByName = new Map(
        objectTypes.map(t => [t.entity_type_name, t.label_plural || t.label])
      );
      return {
        names,
        labels: names.map(name => labelByName.get(name) ?? name),
      };
    },
    [objectTypes, canRead]
  );

  return {
    grantable,
    scopingsByType,
    associationTargetTypes: associationTypeMap,
    writeScopingsByType,
    allowedWriteEventsByType,
    resolveImplied,
    // Association types feed `scopingsByType` (each scoping is classified by its
    // association's target). Reporting ready before they load lets the picker
    // offer scoped types whose scopings resolve empty, which then fails at submit
    // — so keep the flow loading/error until association types are resolved too.
    isLoading: schemaLoading || objectTypesLoading || associationTypesLoading,
    error: schemaError ?? objectTypesError ?? associationTypesError,
  };
}
